import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { eventAPI } from '@services/api';
import { PERFORMANCE } from '@utils/constants';
import type { Event, Photo, EventContextType } from '@/types';

interface EventState {
  events: Event[];
  currentEvent: Event | null;
  photos: Photo[];
  loading: boolean;
  error: string | null;
  eventCache: Record<string, { data: Event; timestamp: number }>;
  photoCache: Record<string, { data: Photo[]; timestamp: number; pagination?: any }>;
  filters: {
    search: string;
    dateRange: [Date, Date] | null;
    eventType: string | null;
    sortBy: 'date' | 'title' | 'photos';
    sortOrder: 'asc' | 'desc';
  };
}

type EventAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_EVENTS'; payload: Event[] }
  | { type: 'SET_CURRENT_EVENT'; payload: Event | null }
  | { type: 'SET_PHOTOS'; payload: Photo[] }
  | { type: 'CACHE_EVENT'; payload: { eventId: string; event: Event } }
  | { type: 'CACHE_PHOTOS'; payload: { eventId: string; photos: Photo[]; pagination?: any } }
  | { type: 'SET_FILTERS'; payload: Partial<EventState['filters']> }
  | { type: 'CLEAR_CACHE' };

const initialState: EventState = {
  events: [],
  currentEvent: null,
  photos: [],
  loading: false,
  error: null,
  eventCache: {},
  photoCache: {},
  filters: {
    search: '',
    dateRange: null,
    eventType: null,
    sortBy: 'date',
    sortOrder: 'desc',
  },
};

const eventReducer = (state: EventState, action: EventAction): EventState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };

    case 'SET_EVENTS':
      return { ...state, events: action.payload, loading: false, error: null };

    case 'SET_CURRENT_EVENT':
      return { ...state, currentEvent: action.payload };

    case 'SET_PHOTOS':
      return { ...state, photos: action.payload, loading: false, error: null };

    case 'CACHE_EVENT':
      return {
        ...state,
        eventCache: {
          ...state.eventCache,
          [action.payload.eventId]: {
            data: action.payload.event,
            timestamp: Date.now(),
          },
        },
      };

    case 'CACHE_PHOTOS':
      return {
        ...state,
        photoCache: {
          ...state.photoCache,
          [action.payload.eventId]: {
            data: action.payload.photos,
            timestamp: Date.now(),
            pagination: action.payload.pagination,
          },
        },
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case 'CLEAR_CACHE':
      return {
        ...state,
        eventCache: {},
        photoCache: {},
      };

    default:
      return state;
  }
};

const EventContext = createContext<EventContextType | null>(null);

interface EventProviderProps {
  children: ReactNode;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(eventReducer, initialState);

  // Cache TTL (5 minutes)
  const CACHE_TTL = PERFORMANCE.CACHE_TTL;

  const isCacheValid = (timestamp: number): boolean => {
    return Boolean(timestamp && Date.now() - timestamp < CACHE_TTL);
  };

  const fetchEvents = useCallback(
    async (filters?: Partial<EventState['filters']>): Promise<void> => {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        const searchParams = filters ? { ...state.filters, ...filters } : state.filters;

        const response = await eventAPI.getEvents({
          search: searchParams.search || undefined,
          sortBy: searchParams.sortBy,
          sortOrder: searchParams.sortOrder,
        });

        dispatch({ type: 'SET_EVENTS', payload: response.data });

        if (filters) {
          dispatch({ type: 'SET_FILTERS', payload: filters });
        }
      } catch (error: any) {
        dispatch({
          type: 'SET_ERROR',
          payload: error.message || 'Failed to load events',
        });
      }
    },
    [state.filters]
  );

  const fetchEvent = useCallback(
    async (eventId: string): Promise<Event> => {
      // Check cache first
      const cached = state.eventCache[eventId];
      if (cached && isCacheValid(cached.timestamp)) {
        dispatch({ type: 'SET_CURRENT_EVENT', payload: cached.data });
        return cached.data;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        const response = await eventAPI.getEvent(eventId);
        const event = response.data;

        dispatch({ type: 'SET_CURRENT_EVENT', payload: event });
        dispatch({ type: 'CACHE_EVENT', payload: { eventId, event } });

        return event;
      } catch (error: any) {
        dispatch({
          type: 'SET_ERROR',
          payload: error.message || 'Failed to load event',
        });
        throw error;
      }
    },
    [state.eventCache]
  );

  const fetchPhotos = useCallback(
    async (eventId: string, page: number = 1): Promise<Photo[]> => {
      // Check cache for first page only
      if (page === 1) {
        const cached = state.photoCache[eventId];
        if (cached && isCacheValid(cached.timestamp)) {
          dispatch({ type: 'SET_PHOTOS', payload: cached.data });
          return cached.data;
        }
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      try {
        const response = await eventAPI.getPhotos(eventId, { page, limit: 50 });
        const { photos, pagination } = response.data;

        if (page === 1) {
          dispatch({ type: 'SET_PHOTOS', payload: photos });
          dispatch({
            type: 'CACHE_PHOTOS',
            payload: { eventId, photos, pagination },
          });
        } else {
          // Append to existing photos for pagination
          const existingPhotos = state.photos;
          const allPhotos = [...existingPhotos, ...photos];
          dispatch({ type: 'SET_PHOTOS', payload: allPhotos });
        }

        return photos;
      } catch (error: any) {
        dispatch({
          type: 'SET_ERROR',
          payload: error.message || 'Failed to load photos',
        });
        throw error;
      }
    },
    [state.photoCache, state.photos]
  );

  const setFilters = useCallback((filters: Partial<EventState['filters']>): void => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const clearCache = useCallback((): void => {
    dispatch({ type: 'CLEAR_CACHE' });
  }, []);

  const value: EventContextType = {
    events: state.events,
    currentEvent: state.currentEvent,
    photos: state.photos,
    loading: state.loading,
    error: state.error,
    filters: state.filters,
    fetchEvents,
    fetchEvent,
    fetchPhotos,
    setFilters,
    clearCache,
  };

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
};

export const useEvents = (): EventContextType => {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error('useEvents must be used within EventProvider');
  }
  return context;
};
